import React, { Fragment, FunctionComponent, useEffect, useState } from 'react';
import {
    Col,
    Row as BSRow,
    FormCheck,
    Table,
    Dropdown,
    DropdownButton,
} from 'react-bootstrap';
import { Cell, Row, downloadSheets, generateSheet } from '../../../utils';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import { I18N } from '../../../i18n-raw';
import {
    AuthedLayout,
    EditableTableDiv,
    HeaderDisplayKeysWithType,
} from '../../../components';
import { useCheckGrade, useClassGrade, useDownloadSheetChart, useSemGrade } from '../../../hook';
import { UnpivotedBloodCheck, CheckTotal } from '../../../model';
import apis from '../../../apis';
import { downloadDataAsExcel, SheetHeaderDisplay } from '../../../utils';
import { toast } from 'react-toastify';
const borderStyle = { color: { rgb: '000000' }, style: 'thin' } as const;
const fullBorderStyle = {
    top: borderStyle,
    bottom: borderStyle,
    left: borderStyle,
    right: borderStyle,
} as const;
const cellAlignStyle = {
    horizontal: 'center',
    vertical: 'center',
} as const;
const mapState = (app: ApplicationState) => ({ ...app.auth });
const mapDispatch = ErrorDispatches;

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;
const checkStastic: FunctionComponent<Props> = ({
    user,
    catchErrorForModal,
}) => {
    const { currentSemester = '', semesters = {} } = user || {};
    const { rank } = user.schools[user.currentSchool];

    const gradeOptionsMap: Record<number, { label: string; value: number }[]> = {
        1: [
            { label: '1年級', value: 1 },
            { label: '4年級', value: 4 },
        ],
        6: [
            { label: '1年級', value: 1 },
            { label: '4年級', value: 4 },
            { label: '7年級', value: 7 },
        ],
        7: [
            { label: '7年級', value: 7 },
        ],
        8: [
            { label: '7年級', value: 7 },
            { label: '10年級', value: 10 },
        ],
        10: [
            { label: '10年級', value: 10 },
        ],
        11: [
            { label: '10年級', value: 10 },
        ],
    };

    const gradeOptions = gradeOptionsMap[rank] ?? [];
    const [currentSem, setCurrentSem] = useState(currentSemester);
    const { year = -1, sem = -1 } = semesters[currentSem];
    const [radioAction, setRadioAction] = useState<boolean>(true);
    const { selectedYearGrade, element: selectgradeElement } = useCheckGrade({ year: year, isNowGrade: radioAction })
    const [selectedGradeValue, setSelectedGradeValue] = useState<number | null>(null);
    const [students, setStudents] = useState<UnpivotedBloodCheck[]>([]);
    const [checktotal, setCheckTotal] = useState<CheckTotal>();

    const handleRadioChange = (isNowGrade: boolean) => {
        setRadioAction(isNowGrade);
    };

    const handleGrageRadioChange = (grade: number) => {
        setSelectedGradeValue(grade);
    };


    useEffect(() => {
        if (selectedYearGrade && selectedGradeValue !== null) {
            toast
                .promise(
                    apis
                        .getCheckStatistic(selectedYearGrade.year, selectedYearGrade?.grade, selectedGradeValue)
                        .then((r) => {
                            setStudents(r.checkData);
                            setCheckTotal(r.checkTotal);
                        }),
                    {
                        pending: '資料載入中......',
                        success: '資料載入成功！',
                        error: '資料載入失敗！請查看錯誤資訊。',
                    }
                )

        }
    }, [selectedYearGrade?.year, selectedYearGrade?.grade, selectedGradeValue]);

    const CategoryBase: Record<string, UnpivotedBloodCheck[]> = students.reduce((categories, d) => {
        return { ...categories, [d.category ?? ""]: [...(categories[d.category ?? ""] || []), d] };
    }, {} as Record<string, UnpivotedBloodCheck[]>);
    const specialItems = ["尿液篩檢", "蟯蟲篩檢"];

    const [normalCategoryBase, specialCategoryBase] = Object.entries(CategoryBase).reduce(
        ([normal, special], [category, list]) => {
            const [specialList, normalList] = list.reduce<[UnpivotedBloodCheck[], UnpivotedBloodCheck[]]>(
                ([s, n], item) => {
                    if (specialItems.includes(item.items)) {
                        s.push(item);
                    } else {
                        n.push(item);
                    }
                    return [s, n];
                },
                [[], []]
            );

            if (normalList.length > 0) normal[category] = normalList;
            if (specialList.length > 0) special[category] = specialList;

            return [normal, special];
        },
        [{}, {}] as [Record<string, UnpivotedBloodCheck[]>, Record<string, UnpivotedBloodCheck[]>]
    );

    const normalRows: Row[] = Object.entries(normalCategoryBase).flatMap(([category, group]) =>
        group.flatMap((g, id) => {
            const itemName = I18N.Physical[g.items] ?? g.items;
            return CategoryToRows(
                category === "血壓" || category === "脈搏" ? "" : category,
                itemName,
                g.m1,
                g.m2,
                g.m3,
                g.m9,
                g.f1,
                g.f2,
                g.f3,
                g.f9,
                checktotal?.mTotal ?? 0,
                checktotal?.fTotal ?? 0,
                id === 0 ? { row: group.length } : undefined
            );
        })
    );

    // 建立 specialRows（尿液、蟯蟲項目）
    const specialRows: Row[] = specialItems.flatMap((item) => {
        const group = Object.values(specialCategoryBase).flat().filter((g) => g.items === item);
        return group.flatMap((g, id) =>
            CategoryToRows(
                "", // 不顯示類別
                item,
                g.m1,
                g.m2,
                g.m3,
                g.m9,
                g.f1,
                g.f2,
                g.f3,
                g.f9,
                checktotal?.mTotal ?? 0,
                checktotal?.fTotal ?? 0,
                id === 0 ? { row: group.length } : undefined
            )
        );
    });

    const contentRows: Row[] = [...normalRows, ...specialRows];

    const content: Row[] = [
        {
            cells: [
                {
                    value: `${user.schools[user.currentSchool].name}${selectedYearGrade?.year == null ? "" : selectedYearGrade?.year}學年${selectedYearGrade?.grade == null ? "" : selectedYearGrade?.grade}年級身體診察結果暨矯治追蹤統計表`,
                    merge: { column: 18 },
                },
            ],
        },
        {
            cells: [
                {
                    value: `類別`,
                    merge: { column: 6 },
                },
                {
                    value: `男`,
                    merge: { column: 4 },
                },
                {
                    value: `女`,
                    merge: { column: 4 },
                }, {
                    value: `合計`,
                    merge: { column: 4 },
                },
            ],
        },
        {
            cells: [
                {
                    value: `受檢學生總數`,
                    merge: { column: 6 },
                },
                {
                    value: checktotal?.mTotal ?? 0,
                    merge: { column: 4 },
                },
                {
                    value: checktotal?.fTotal ?? 0,
                    merge: { column: 4 },
                }, {
                    value: (checktotal?.fTotal ?? 0) + (checktotal?.mTotal ?? 0),
                    merge: { column: 4 },
                },
            ],
        },
        {
            cells: [
                {
                    value: `健檢無異狀學生數`,
                    merge: { column: 6 },
                },
                {
                    value: checktotal?.noProblemM ?? 0,
                    merge: { column: 4 },
                },
                {
                    value: checktotal?.noProblemF ?? 0,
                    merge: { column: 4 },
                }, {
                    value: (checktotal?.noProblemM ?? 0) + (checktotal?.noProblemF ?? 0),
                    merge: { column: 4 },
                },
            ],
        },
        {
            cells: [
                {
                    value: '科別',
                    merge: { column: 1, row: 3 },
                },
                {
                    value: '檢查名稱/統計',
                    merge: { column: 1, row: 3 },
                },
                {
                    value: '未實施人數',
                    merge: { column: 2, row: 1 },
                },
                {
                    value: '檢查項目結果發現異常',
                    merge: { column: 6, row: 1 },
                },
                {
                    value: '複檢就醫矯治追蹤情形',
                    merge: { column: 8, row: 1 },
                }
            ],
        },
        {
            cells: [
                {
                    value: '男',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '女',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '男',
                    merge: { column: 2, row: 1 },
                },
                {
                    value: '女',
                    merge: { column: 2, row: 1 },
                },
                {
                    value: '合計*',
                    merge: { column: 2, row: 1 },
                },
                {
                    value: '複檢正常☆',
                    merge: { column: 2, row: 1 },
                },
                {
                    value: '複檢異常△',
                    merge: { column: 2, row: 1 },
                },
                {
                    value: '未就醫×',
                    merge: { column: 2, row: 1 },
                },
                {
                    value: '就醫率',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '備註﹝及其他異常項目﹞',
                    merge: { column: 1, row: 1 },
                }
            ],
        },
        {
            cells: [
                {
                    value: '人數',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '人數',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '人數',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '%',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '人數',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '%',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '人數',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '%',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '人數',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '%',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '人數',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '%',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '人數',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '%',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '%',
                    merge: { column: 1, row: 1 },
                },
                {
                    value: '',
                    merge: { column: 1, row: 1 },
                },
            ],
        },
        ...contentRows,
    ];

    const tableworkSheet: Row[] = [
        ...content,
        {
            cells: [
                {
                    value: '說明：',
                    merge: { column: 18, },
                    style: { alignment: { horizontal: 'left' } },
                },
            ],
        },
        {
            cells: [
                {
                    value: '一、本表置於教育部網站體育司業務介紹之學校衛生項之健康檢查，請依「班級健康檢查結果暨矯治追蹤名冊」一、四、七年級分開統計。',
                    merge: { column: 18, },
                    style: { alignment: { horizontal: 'left' } },
                },
            ],
        },
        {
            cells: [
                {
                    value: '二、異狀百分比=異狀人數÷受檢總人數，就醫率=﹝☆+○﹞÷﹝＊﹞',
                    merge: { column: 18, },
                    style: { alignment: { horizontal: 'left' } },
                },
            ],
        },
        {
            cells: [
                {
                    value: '三、健康檢查於上學期執行者本表於二月底前上傳完成,並製作一式兩份，一份留存學校備查，一份送教育局體健課。',
                    merge: { column: 18, },
                    style: { alignment: { horizontal: 'left' } },
                },
            ],
        },
        {
            cells: [
                {
                    value: '四、視力不良是指E字視力檢查表篩檢值任一眼低於0.8以下(含0.8), 近視、遠視、散光、斜弱視的判讀是指經眼科醫師診斷後的判讀結果 ;「辨色力異常」指以色盲檢查卡檢出辨色異狀人數。 ',
                    merge: { column: 18, },
                    style: { alignment: { horizontal: 'left' } },
                },
            ],
        },
        {
            cells: [
                {
                    value: '五、蟯蟲陽性的統計，請填寫與健康檢查同學期之第一次蟯蟲檢查陽性的人數。',
                    merge: { column: 18, },
                    style: { alignment: { horizontal: 'left' } },
                },
            ],
        },
        {
            cells: [
                {
                    value: '六 、近視﹑遠視﹑散光為視力不良分類診斷,故不計算矯治率。',
                    merge: { column: 18, },
                    style: { alignment: { horizontal: 'left' } },
                },
            ],
        },
    ];

    const {
        downloadChart,
        disabled: exportDisabled,
    } = useDownloadSheetChart({
        chartdata: [
            checktotal?.mTotal ?? 0,
            checktotal?.fTotal ?? 0,
        ],
        chartRefs: [],
        titles: [
            `${user.schools[user.currentSchool].name}${selectedYearGrade?.year ?? ''}學年${selectedYearGrade?.gradeName ?? ''}年級身體診察結果暨矯治追蹤統計表`,
        ],
        tablesheets: tableworkSheet,
        tablesheetname: '身體診察結果暨矯治追蹤統計表',
    });

    const handlePrint = () => {
        const printWindow = window.open(
            '',
            '_blank',
            'width=1500,height=900'
        );

        if (!printWindow) {
            alert('瀏覽器阻擋了列印視窗，請允許彈出式視窗。');
            return;
        }

        const escapeHtml = (value: unknown): string => {
            return String(value ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        };

        const title =
            `${user.schools[user.currentSchool].name}${selectedYearGrade?.year ?? ''}學年${selectedYearGrade?.gradeName ?? ''}年級身體診察結果暨矯治追蹤統計表`;

        const tableRows = tableworkSheet
            .map((row) => {
                const cells = row.cells
                    .map((cell) => {
                        if (cell.value === undefined) {
                            return '';
                        }

                        const colSpan = cell.merge?.column
                            ? ` colspan="${cell.merge.column}"`
                            : '';

                        const rowSpan = cell.merge?.row
                            ? ` rowspan="${cell.merge.row}"`
                            : '';

                        return `
                        <td${colSpan}${rowSpan}>
                            ${escapeHtml(cell.value)}
                        </td>
                    `;
                    })
                    .join('');

                return `<tr>${cells}</tr>`;
            })
            .join('');

        printWindow.document.open();

        printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="zh-Hant">
        <head>
            <meta charset="UTF-8" />
            <title>${escapeHtml(title)}</title>

            <style>
                * {
                    box-sizing: border-box;
                }

                html,
                body {
                    margin: 0;
                    padding: 0;
                    font-family:
                        "Microsoft JhengHei",
                        "Noto Sans TC",
                        Arial,
                        sans-serif;
                    color: #000;
                }

                .print-page {
                    min-height: 190mm;
                    display: flex;
                    flex-direction: column;
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                    table-layout: fixed;
                }

                td {
                    border: 1px solid #000;
                    padding: 3px 2px;
                    text-align: center;
                    vertical-align: middle;
                    font-size: 8px;
                    word-break: break-word;
                }

                tr:first-child td {
                    font-size: 14px;
                    font-weight: bold;
                    padding: 8px;
                }

                .footer {
                    margin-top: auto;
                    width: 100%;
                    padding-top: 15px;
                    display: flex;
                    justify-content: space-between;
                    font-size: 10px;
                }

                @page {
                    size: A4 landscape;
                    margin: 6mm;
                }

                @media print {
                    body {
                        padding: 0;
                    }

                    tr {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                }
            </style>
        </head>

        <body>
            <div class="print-page">

                <table>
                    <tbody>
                        ${tableRows}
                    </tbody>
                </table>

                <div class="footer">
                    <span>承辦人:</span>
                    <span>組長:</span>
                    <span>主任:</span>
                    <span>校長:</span>
                </div>

            </div>

            <script>
                window.onload = function () {
                    window.focus();

                    setTimeout(function () {
                        window.print();
                    }, 300);
                };

                window.onafterprint = function () {
                    window.close();
                };
            </script>
        </body>
        </html>
    `);

        printWindow.document.close();
    };

    return (
        <AuthedLayout>
            <BSRow className="justify-content-between">
                <Col className='d-flex mr-3' xs={3} >
                    <FormCheck
                        className="mr-2"
                        value={'在學'}
                        type="radio"
                        label={`在學`}
                        name="list-action"
                        checked={radioAction === true}
                        onChange={() => handleRadioChange(true)}
                    />
                    <FormCheck
                        className="mr-2"
                        value={`歷屆年級`}
                        type="radio"
                        label={`歷屆年級`}
                        name="list-action"
                        checked={radioAction === false}
                        onChange={() => handleRadioChange(false)}
                    />
                </Col>
                <Col xs={3} className="mr-3">
                    {selectgradeElement}
                </Col>
                <Col className='d-flex mr-3' xs={3} >
                    {gradeOptions.map((option) => (
                        <FormCheck
                            key={option.value}
                            className="mr-2"
                            type="radio"
                            name="list-action-grade"
                            label={option.label}
                            value={option.value}
                            checked={selectedGradeValue === option.value}
                            onChange={() => {
                                setSelectedGradeValue(option.value);
                                handleGrageRadioChange(option.value); // 呼叫原本的處理函式
                            }}
                        />
                    ))}
                </Col>
                <div>
                    <DropdownButton
                        id="check-static-export"
                        title="匯出"
                        variant="success"
                        disabled={exportDisabled}
                    >
                        <Dropdown.Item onClick={downloadChart}>
                            Excel 下載
                        </Dropdown.Item>

                        <Dropdown.Item onClick={handlePrint}>
                            列印
                        </Dropdown.Item>
                    </DropdownButton>
                </div>
            </BSRow>
            <hr />
            <BSRow>
                <Table
                    striped
                    bordered
                    className="text-center"
                    style={{ tableLayout: 'fixed' }}
                >
                    <tbody>
                        {content.map((r, i) => {
                            const cells = r.cells.map((c, j) =>
                                c.value != undefined ? (
                                    <td
                                        colSpan={c.merge?.column}
                                        rowSpan={c.merge?.row}
                                        key={`${i}-${j}`}
                                    >
                                        {c.value}
                                    </td>
                                ) : (
                                    <Fragment key={`${i}-${j}`}></Fragment>
                                )
                            );
                            return <tr key={i}>{cells}</tr>;
                        })}
                    </tbody>
                </Table>
            </BSRow>
        </AuthedLayout>
    );
};

function CategoryToRows(
    category: string,
    item: string,
    m1: number,
    m2: number,
    m3: number,
    m9: number,
    f1: number,
    f2: number,
    f3: number,
    f9: number,
    mtotal: number,
    ftotal: number,
    categoryMerge?: { row?: number; column?: number },
): Row[] {
    const skipDetailsItems = [
        "已治療齲齒",
        "上顎恆牙第一大臼齒齲齒經驗",
        "下顎恆牙第一大臼齒齲齒經驗",
        "恆牙臼齒窩溝封填",
    ];
    const shouldSkipDetails = skipDetailsItems.includes(item);
    const gradeNo = [];
    if (!categoryMerge?.column && !categoryMerge?.row) {
        gradeNo.push({
            value: item,
            merge: {},
            style: { border: fullBorderStyle },
        });
    } else {
        gradeNo.push(
            {
                value: categoryMerge ? category : undefined,
                merge: categoryMerge,
                style: { border: fullBorderStyle },
            },
            {
                value: item,
                merge: {},
                style: { border: fullBorderStyle },
            });
    }

    const mTotalAbnormal = (m1 ?? 0) + (m2 ?? 0) + (m3 ?? 0);
    const fTotalAbnormal = (f1 ?? 0) + (f2 ?? 0) + (f3 ?? 0);
    const totalAbnormal = mTotalAbnormal + fTotalAbnormal;

    return [
        {
            cells: [
                ...gradeNo,
                { value: m9 ?? 0, merge: {}, style: { border: { left: borderStyle } } },
                { value: f9 ?? 0, merge: {}, style: { border: { left: borderStyle } } },
                { value: mTotalAbnormal, merge: {}, style: { border: { left: borderStyle } } },
                {
                    value: percent(mTotalAbnormal, mtotal - m9) ?? "0%",
                    merge: {},
                    style: { border: { left: borderStyle } },
                },
                { value: fTotalAbnormal, merge: {}, style: { border: { left: borderStyle } } },
                {
                    value: percent(fTotalAbnormal, ftotal - f9) ?? "0%",
                    merge: {},
                    style: { border: { left: borderStyle } },
                },
                { value: totalAbnormal, merge: {}, style: { border: { left: borderStyle } } },
                {
                    value: percent(
                        totalAbnormal,
                        getDenominator(category, mtotal, ftotal, f9, m9)
                    ) ?? "0%",
                    merge: {},
                    style: { border: { left: borderStyle } },
                },

                // 複檢正常人數
                shouldSkipDetails
                    ? { value: '', merge: {}, style: { border: { left: borderStyle } } }
                    : { value: (m2 ?? 0) + (f2 ?? 0), merge: {}, style: { border: { left: borderStyle } } },

                // 複檢正常 %
                shouldSkipDetails
                    ? { value: '', merge: {}, style: { border: { left: borderStyle } } }
                    : {
                        value: totalAbnormal === 0 ? '' : percent((m2 ?? 0) + (f2 ?? 0), totalAbnormal) ?? "0%",
                        merge: {},
                        style: { border: { left: borderStyle } },
                    },

                // 複檢異常人數
                shouldSkipDetails
                    ? { value: '', merge: {}, style: { border: { left: borderStyle } } }
                    : { value: (m3 ?? 0) + (f3 ?? 0), merge: {}, style: { border: { left: borderStyle } } },

                // 複檢異常 %
                shouldSkipDetails
                    ? { value: '', merge: {}, style: { border: { left: borderStyle } } }
                    : {
                        value: totalAbnormal === 0 ? '' : percent((m3 ?? 0) + (f3 ?? 0), totalAbnormal) ?? "0%",
                        merge: {},
                        style: { border: { left: borderStyle } },
                    },

                // 初檢未複檢人數
                shouldSkipDetails
                    ? { value: '', merge: {}, style: { border: { left: borderStyle } } }
                    : {
                        value: totalAbnormal - ((m2 ?? 0) + (f2 ?? 0) + (m3 ?? 0) + (f3 ?? 0)),
                        merge: {},
                        style: { border: { left: borderStyle } },
                    },

                // 初檢未複檢 %
                shouldSkipDetails
                    ? { value: '', merge: {}, style: { border: { left: borderStyle } } }
                    : {
                        value: totalAbnormal === 0
                            ? ''
                            : percent(
                                totalAbnormal - ((m2 ?? 0) + (f2 ?? 0) + (m3 ?? 0) + (f3 ?? 0)),
                                totalAbnormal
                            ) ?? "0%",
                        merge: {},
                        style: { border: { left: borderStyle } },
                    },

                // 複檢比例 %
                shouldSkipDetails
                    ? { value: '', merge: {}, style: { border: { left: borderStyle } } }
                    : {
                        value: totalAbnormal === 0
                            ? ''
                            : percent((m2 ?? 0) + (f2 ?? 0) + (m3 ?? 0) + (f3 ?? 0), totalAbnormal) ?? "0%",
                        merge: {},
                        style: { border: { left: borderStyle } },
                    },

                { value: '', merge: {}, style: { border: { left: borderStyle } } },
            ],
        },
    ];
}



function getDenominator(category: string, mtotal: number, ftotal: number, f9: number, m9: number): number {
    return category === "泌尿生殖" ? mtotal - m9 : (mtotal + ftotal) - (f9 - m9);
}

function percent(value: number, denominator: number): string {
    if (!denominator) return '0.00%';
    return ((value / denominator) * 100).toFixed(2) + '%';
}
export const CheckStastic = connector(checkStastic);